/**
 * separateObjectByPrefix is a function that will take a single
 * object and separate it into multiple objects bases on a list of keys passed in.
 * @type {<Keys extend Array<string>(obj: Record<string, unknown>, keys: Keys) => Record<Keys, unknown>}
 */
export const separateObjectByPrefix = (obj, keys) => {
  const result = {};
  Object.keys(obj).forEach((key) => {
    let used = false;
    keys.forEach((prefix) => {
      if (key.startsWith(prefix) && !used) {
        used = true;
        if (typeof result[prefix] !== "object") {
          result[prefix] = {};
        }
        const [property, id] = key.split(":");
        if (id) {
          if (typeof result[prefix][id] !== "object") {
            result[prefix][id] = {};
          }
          result[prefix][id][property] = obj[key];
        } else {
          result[prefix][property] = obj[key];
        }
      }
    });
    if (!used) {
      if (typeof result.rest !== "object") {
        result.rest = {};
      }
      result.rest[key] = obj[key];
    }
  });
  return result;
};

export const isPositiveNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
};

export const generateAutoOfferDescription = ({
  dollarAmount,
  percentAmount,
  metadata = {}
}) => {
  const hasPositiveDollarAmount = isPositiveNumber(dollarAmount);
  const hasPositivePercentAmount = isPositiveNumber(percentAmount);

  if (!hasPositiveDollarAmount && !hasPositivePercentAmount) {
    return "";
  }

  if (hasPositiveDollarAmount && hasPositivePercentAmount) {
    return "";
  }

  let description = "";
  if (hasPositiveDollarAmount) {
    const parsedDollar = Number(dollarAmount);
    description = `$${parsedDollar.toFixed(0)} off`;
  } else if (hasPositivePercentAmount) {
    const parsedPercent = Number(percentAmount);
    const formattedPercent = parsedPercent.toLocaleString(undefined, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0
    });
    description = `${formattedPercent}% off`;
  }

  const minimumPurchaseRaw = metadata?.voucherMinimumPurchase;

  if (
    minimumPurchaseRaw !== undefined &&
    minimumPurchaseRaw !== null &&
    minimumPurchaseRaw !== ""
  ) {
    const numericMinimumPurchase = Number(minimumPurchaseRaw);
    if (Number.isFinite(numericMinimumPurchase) && numericMinimumPurchase > 0) {
      const formattedMinimumPurchase =
        typeof minimumPurchaseRaw === "string"
          ? minimumPurchaseRaw.trim()
          : numericMinimumPurchase.toString();
      description += ` of $${formattedMinimumPurchase}+`;
    }
  }

  return description;
};

const normalizeNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const buildOfferPayload = ({
  offerChanges,
  metadataChanges,
  existingOffer,
  isNewOffer,
  vendorImageURL
}) => {
  const safeOfferChanges = { ...offerChanges };
  const existingMetadata = existingOffer?.metadata || {};
  const mergedMetadata = metadataChanges
    ? {
      ...existingMetadata,
      ...metadataChanges
    }
    : existingMetadata;

  const metadataWithDefaults = isNewOffer
    ? {
      offerType: "gift card",
      ...mergedMetadata
    }
    : mergedMetadata;

  const resolvedRewardRedemption = Object.hasOwn(safeOfferChanges, "rewardRedemption")
    ? Boolean(safeOfferChanges.rewardRedemption)
    : existingOffer?.rewardRedemption ?? true;

  if (!Object.hasOwn(safeOfferChanges, "rewardRedemption")) {
    safeOfferChanges.rewardRedemption = resolvedRewardRedemption;
  } else {
    safeOfferChanges.rewardRedemption = Boolean(safeOfferChanges.rewardRedemption);
  }

  const resolvedDollarAmount = Object.hasOwn(safeOfferChanges, "dollarAmount")
    ? safeOfferChanges.dollarAmount
    : existingOffer?.dollarAmount ?? null;

  const resolvedPercentAmount = Object.hasOwn(safeOfferChanges, "percentAmount")
    ? safeOfferChanges.percentAmount
    : existingOffer?.percentAmount ?? null;

  const resolvedDescriptionCandidate = Object.hasOwn(safeOfferChanges, "offerDescription")
    ? safeOfferChanges.offerDescription
    : existingOffer?.offerDescription ?? "";

  const normalizedDescription =
    typeof resolvedDescriptionCandidate === "string"
      ? resolvedDescriptionCandidate.trim()
      : "";

  const shouldApplyVendorImageFallback =
    !Object.hasOwn(safeOfferChanges, "offerImage") &&
    !existingOffer?.offerImage &&
    vendorImageURL;

  if (shouldApplyVendorImageFallback) {
    safeOfferChanges.offerImage = vendorImageURL;
  }

  const autoGeneratedDescription = generateAutoOfferDescription({
    dollarAmount: normalizeNumber(resolvedDollarAmount),
    percentAmount: normalizeNumber(resolvedPercentAmount),
    metadata: metadataWithDefaults
  }).trim();

  if (!normalizedDescription && autoGeneratedDescription.length > 0) {
    safeOfferChanges.offerDescription = autoGeneratedDescription;
  }

  // Normalize numeric fields to null instead of empty strings
  if (Object.hasOwn(safeOfferChanges, "dollarAmount")) {
    safeOfferChanges.dollarAmount = normalizeNumber(safeOfferChanges.dollarAmount);
  }
  if (Object.hasOwn(safeOfferChanges, "percentAmount")) {
    safeOfferChanges.percentAmount = normalizeNumber(safeOfferChanges.percentAmount);
  }
  if (Object.hasOwn(safeOfferChanges, "pointAmount")) {
    safeOfferChanges.pointAmount = normalizeNumber(safeOfferChanges.pointAmount);
  }

  // Normalize voucherMinimumPurchase in metadata
  if (metadataWithDefaults?.voucherMinimumPurchase !== undefined) {
    metadataWithDefaults.voucherMinimumPurchase = normalizeNumber(metadataWithDefaults.voucherMinimumPurchase);
  }

  const shouldIncludeMetadata =
    Boolean(metadataChanges) || (isNewOffer && Object.keys(metadataWithDefaults).length > 0);

  if (shouldIncludeMetadata) {
    safeOfferChanges.metadata = metadataWithDefaults;
  }

  return safeOfferChanges;
};
